import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Container,
  Paper,
  Link as MuiLink,
  Breadcrumbs,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LaunchIcon from '@mui/icons-material/Launch';
import { getActivityDetail } from '../services/api';
import { formatRelativeTime } from '../utils/dateUtils';
import ActivityReactions from '../components/common/ActivityReactions';

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

interface ActivityDetail {
  id: number;
  type: number;
  user: {
    id: number;
    username: string;
    display_name: string;
    avatar: string | null;
  };
  game: {
    id: number;
    name: string;
    cover_image: string | null;
  };
  created_at: string;
  old_status?: number;
  new_status?: number;
  review_text?: string;
  reactions?: { [key: number]: number };
  user_reaction?: number | null;
}

const getStatusText = (status: number) => {
  switch (status) {
    case 1: return 'プレイ済み';
    case 2: return 'プレイ中';
    case 3: return '中断';
    case 4: return '積みゲー';
    case 5: return 'ウィッシュリスト';
    default: return '';
  }
};

const getActionText = (type: number, oldStatus?: number, newStatus?: number) => {
  if (type === 1 && newStatus === 5) {
    return null;
  }

  if (type === 1) {
    switch (newStatus) {
      case 1: return 'プレイ済みに追加';
      case 2: return 'プレイを開始';
      case 3: return '中断に追加';
      case 4: return '積みゲーに追加';
      default: return '';
    }
  }

  if (type === 2) {
    if (oldStatus === 1 && newStatus === 2) {
      return 'プレイ済みタイトルのプレイを再開';
    }
    if (oldStatus === 2 && newStatus === 1) {
      return 'プレイを終えた';
    }
    if (oldStatus === 2 && newStatus === 3) {
      return 'プレイを中断';
    }
    if (oldStatus === 3 && newStatus === 2) {
      return '中断していたタイトルのプレイを再開';
    }
    if (oldStatus === 4 && newStatus === 1) {
      return '積みゲーを消化';
    }
    if (oldStatus === 4 && newStatus === 2) {
      return '積みゲーのプレイを開始';
    }
    if (oldStatus === 5 && newStatus === 2) {
      return 'ウィッシュリストのゲームを開始';
    }
    if (oldStatus === 5 && newStatus === 4) {
      return 'ウィッシュリストのゲームを積んだ';
    }
    if (newStatus !== 5) {
      return `${getStatusText(oldStatus || 0)}から${getStatusText(newStatus || 0)}に変更`;
    }
    return null;
  }

  if (type === 3) return 'レビューを投稿';
  if (type === 4) return 'レビューを更新';

  return null;
};

const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

const ActivityDetailPage: React.FC = () => {
  const { userId, activityId } = useParams<{ userId: string; activityId: string }>();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!userId || !activityId) return;

      try {
        setLoading(true);
        const data = await getActivityDetail(parseInt(userId), parseInt(activityId));
        setActivity(data);
      } catch (err) {
        setError('アクティビティが見つかりませんでした');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [userId, activityId]);

  const handleReactionUpdate = (activityId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => {
    if (activity) {
      setActivity({ ...activity, reactions: newReactions, user_reaction: newUserReaction });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !activity) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">{error || 'エラーが発生しました'}</Typography>
        </Paper>
      </Container>
    );
  }

  const actionText = getActionText(activity.type, activity.old_status, activity.new_status);
  if (actionText === null) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">このアクティビティは表示できません</Typography>
        </Paper>
      </Container>
    );
  }

  const isReviewActivity = activity.type === 3 || activity.type === 4;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* パンくずリスト */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3, color: 'text.secondary', fontSize: { xs: '0.7rem', md: '0.8rem' } }}
      >
        <MuiLink
          component={RouterLink}
          to={`/game/${activity.game.id}`}
          color="inherit"
          sx={{ '&:hover': { color: 'primary.main' } }}
        >
          {activity.game.name}
        </MuiLink>
        <Typography color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
          アクティビティ
        </Typography>
      </Breadcrumbs>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* メインコンテンツ */}
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 } }}>
            {/* ゲームカバー */}
            <Box
              sx={{ flexShrink: 0, cursor: 'pointer' }}
              onClick={() => navigate(`/game/${activity.game.id}`)}
            >
              <img
                src={getCoverUrl(activity.game.cover_image)}
                alt={activity.game.name}
                style={{
                  width: 80,
                  height: 107,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              />
            </Box>

            {/* 右側コンテンツ */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* ユーザー情報 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Avatar
                  src={activity.user.avatar || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/profile/${activity.user.username}`)}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => navigate(`/profile/${activity.user.username}`)}
                >
                  {activity.user.display_name}
                </Typography>
              </Box>
              {/* 日時 */}
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 1 }}
              >
                {formatDateTime(activity.created_at)}
                <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                  （{formatRelativeTime(activity.created_at)}）
                </Box>
              </Typography>

              {/* アクションテキスト（メイン情報） */}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  color: '#ffffff',
                  mb: 1.5,

                  lineHeight: 1.3
                }}
              >
                {actionText}
                {isReviewActivity && activity.review_text && (
                  <MuiLink
                    component={RouterLink}
                    to={`/profile/${activity.user.username}?tab=reviews`}
                    sx={{
                      ml: 1,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    レビューを見る
                    <LaunchIcon sx={{ fontSize: '0.7rem' }} />
                  </MuiLink>
                )}
              </Typography>

              {/* ゲーム名 */}
              <MuiLink
                component={RouterLink}
                to={`/game/${activity.game.id}`}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  display: 'block',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
              >
                {activity.game.name}
              </MuiLink>
            </Box>
          </Box>
        </Box>

        {/* リアクションエリア */}
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            pb: { xs: 1, md: 1.5 },
            pt: 0.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <ActivityReactions
            activityId={activity.id}
            reactions={activity.reactions || {}}
            userReaction={activity.user_reaction || null}
            isPublic={true}
            onReactionUpdate={handleReactionUpdate}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default ActivityDetailPage;
